/* StarWays 2.0 branding — arch mark (two arcs + sparkle) + Bricolage wordmark */
import React, { useEffect, useState } from "react";

export const BRAND = { emerald: "#0A3D2E", gold: "#C8A961", cream: "#F3EBD8" };

/* Mark geometry in a 340×240 box (shared with canvas/story renderer) */
export const MARK_PATHS = {
  topArc: "M 14 18 Q 170 52 326 18",
  bottomArc: "M 14 222 Q 170 188 326 222",
  star: "M 170 57 C 176 96 192 112 223 120 C 192 128 176 144 170 183 C 164 144 148 128 117 120 C 148 112 164 96 170 57 Z",
  dashL: "M 22 120 L 92 120",
  dashR: "M 248 120 L 318 120",
};

let markAnimated = false;

export const SwMark = ({ height = 28, arc = BRAND.emerald, gold = BRAND.gold, animate = false, className = "" }) => {
  const [play] = useState(() => animate && !markAnimated);
  useEffect(() => { if (play) markAnimated = true; }, [play]);
  const small = height < 40; // optical size: heavier strokes + bigger sparkle when tiny
  return (
    <svg
      viewBox="0 0 340 240"
      height={height}
      width={(height * 340) / 240}
      xmlns="http://www.w3.org/2000/svg"
      className={`sw-mark ${play ? "sw-mark--intro" : ""} ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <g fill="none" stroke={arc} strokeWidth={small ? 24 : 19} strokeLinecap="round">
        <path className="sw-mark__top" d={MARK_PATHS.topArc} />
        <path className="sw-mark__bottom" d={MARK_PATHS.bottomArc} />
      </g>
      <g stroke={gold} strokeWidth={small ? 12 : 9} strokeLinecap="round">
        <path className="sw-mark__dash sw-mark__dash--l" d={MARK_PATHS.dashL} />
        <path className="sw-mark__dash sw-mark__dash--r" d={MARK_PATHS.dashR} />
      </g>
      <g transform={small ? "translate(170 120) scale(1.14) translate(-170 -120)" : undefined}>
        <path className="sw-mark__star" d={MARK_PATHS.star} fill={gold} />
      </g>
    </svg>
  );
};

/* Back-compat alias */
export const SwStar = ({ size = 28, color = BRAND.gold, className = "" }) => (
  <SwMark height={size} gold={color} arc={color} className={className} />
);

/**
 * StarWaysLogo — horizontal lock-up. `tone="dark"` for dark backgrounds.
 */
export const StarWaysLogo = ({ size = 24, className = "", tone = "light", animate = false, ink, gold }) => {
  const text = ink || (tone === "dark" ? "currentColor" : BRAND.emerald);
  return (
    <span className={`sw-logo sw-logo--${tone} inline-flex items-center ${className}`} style={{ gap: size * 0.45 }} role="img" aria-label="StarWays">
      <SwMark height={size * 1.4} arc={text} gold={gold || BRAND.gold} animate={animate} />
      <span
        aria-hidden="true"
        style={{
          color: text,
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: `${size}px`,
          letterSpacing: "-0.035em",
          lineHeight: 1,
          fontStyle: "normal",
        }}
      >
        <span style={{ fontWeight: 800 }}>Star</span>
        <span style={{ fontWeight: 400, letterSpacing: "-0.02em" }}>Ways</span>
      </span>
    </span>
  );
};

/* ---------- Legacy exports (kept for not-yet-redesigned pages) ---------- */

export const Mark = ({ size = 36, className = "" }) => (
  <span className="sw-logo--dark" style={{ display: "inline-block" }}><SwMark height={size} arc="currentColor" className={className} /></span>
);

export const Wordmark = ({ className = "", color = "#C9A961", size = "md" }) => {
  const sizePx = size === "sm" ? 16 : size === "lg" ? 36 : size === "xl" ? 48 : 24;
  return <StarWaysLogo size={sizePx} className={className} tone="dark" />;
};

export const StackedLogo = ({ size = "md", color = "#C9A961", className = "" }) => (
  <Wordmark className={className} color={color} size={size} />
);

export const Hairline = ({ className = "", color = "#C9A961" }) => (
  <div className={className} style={{ height: "1px", background: `linear-gradient(to right, transparent, ${color}66, transparent)` }} />
);

export const SectionLabel = ({ num, title, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    {num && <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 600, color: "#C9A961" }}>{num}</span>}
    <span className="eyebrow-soft">{title}</span>
  </div>
);

export const SectionDivider = ({ className = "", color = "#C9A961" }) => (
  <div aria-hidden="true" className={`relative w-full flex items-center justify-center py-8 sm:py-10 ${className}`}>
    <span className="flex-1 h-px max-w-[280px]" style={{ background: `linear-gradient(to right, transparent, ${color}33 60%, ${color}66)` }} />
    <svg viewBox="0 0 140 26" width="140" height="26" xmlns="http://www.w3.org/2000/svg" className="mx-4 sm:mx-6 flex-shrink-0" role="presentation">
      <path d="M 6 18 Q 70 6 134 18" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 70 10 L 74 13 L 70 16 L 66 13 Z" fill={color} opacity="0.9" />
    </svg>
    <span className="flex-1 h-px max-w-[280px]" style={{ background: `linear-gradient(to left, transparent, ${color}33 60%, ${color}66)` }} />
  </div>
);
